import { Injectable } from '@angular/core';
import { HttpClient, HttpEventType, HttpParams, HttpRequest } from '@angular/common/http';
import { AttachmentBase } from 'src/sb-tools/typescript/models/attachment-base.model';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs/internal/Observable';
import { Subject } from 'rxjs/internal/Subject';
import { environment } from '@environment';
import { FileModelExtended } from '../models/FileModelExtended.model';

@Injectable({ providedIn: 'root' })
export class AttachmentService {
    isForwardingFileFinished = new Subject<boolean>();
    isUploadingFilesFinished = new Subject<boolean>();

    preventedFileExtensionList = ['APP', 'BIN', 'EXE', 'OSX', 'OUT', 'PRG', 'RUN', 'SCR', 'BAT', 'MSI'];

    constructor(
        private httpClient: HttpClient,
        private translateService: TranslateService,
    ) {}

    changeUploadingStatus(isFinished: boolean): void {
        this.isUploadingFilesFinished.next(isFinished);
    }

    changeForwardingStatus(isFinished: boolean): void {
        this.isForwardingFileFinished.next(isFinished);
    }

    deleteFile(attachmentUrl: string): Observable<void> {
        return this.httpClient.delete<void>(environment.uploadsUrl + 'attachment/', {
            params: { attachmentUrl },
        });
    }

    downloadFile(urlToBlobAttachment: string): Observable<Blob | MediaSource> {
        return this.httpClient.get(environment.uploadsUrl + 'attachments/', {
            headers: {},
            params: new HttpParams().set('url', urlToBlobAttachment),
            responseType: 'blob',
        });
    }

    getDropzoneConfig(
        url: string,
        maxFilesize = 25,
        uploadMultiple?: string,
        acceptedFiles = '',
        existedAttachmentsLength?: number,
        clickableZoneSelector?: string,
        maxAttachmentsCount = 1000,
        headers = {},
    ) {
        if (maxAttachmentsCount === null || maxAttachmentsCount === undefined) {
            maxAttachmentsCount = 1000;
        }
        return {
            url,
            maxFilesize,
            paramName: 'uploadfile',
            maxThumbnailFilesize: 5,
            method: 'post',
            uploadMultiple: false,
            maxFiles: uploadMultiple === 'false' ? 1 : maxAttachmentsCount - existedAttachmentsLength,
            acceptedFiles: uploadMultiple === 'false' && !acceptedFiles ? 'image/*' : acceptedFiles,
            addRemoveLinks: true,
            filesizeBase: 1024,
            clickable: clickableZoneSelector ? clickableZoneSelector : true,
            timeout: 30000000,
            dictDefaultMessage: this.translateService.instant('Drop files here to upload'),
            dictFallbackMessage: this.translateService.instant(
                "Your browser does not support drag'n'drop file uploads.",
            ),
            dictFallbackText: this.translateService.instant(
                'Please use the fallback form below to upload your files like in the olden days.',
            ),
            dictFileTooBig:
                this.translateService.instant('File is too big ') +
                '({{filesize}}' +
                this.translateService.instant('MB') +
                '). ' +
                this.translateService.instant('Max file size: ') +
                '{{maxFilesize}}' +
                this.translateService.instant('MB'),
            dictInvalidFileType: this.translateService.instant("You can't upload files of this type."),
            dictResponseError: this.translateService.instant('Server responded with {{statusCode}} code.'),
            dictRemoveFile: this.translateService.instant('Remove file'),
            dictCancelUpload: this.translateService.instant('Cancel upload'),
            dictCancelUploadConfirmation: this.translateService.instant('Are you sure you want to cancel this upload?'),
            dictMaxFilesExceeded: this.translateService.instant('You can not upload any more files.'),
            headers: {
                // remove Cache-Control and X-Requested-With to be sent along with the request
                'Cache-Control': null,
                'X-Requested-With': null,
                ...headers,
            },
            accept: (file: File, done): void => {
                const mask = /(?:\.([^.]+))?$/;
                const extension = mask.exec(file.name)[1]?.toUpperCase();

                if (
                    file.type === 'application/x-msdownload' ||
                    this.preventedFileExtensionList.indexOf(extension) > -1
                ) {
                    done(this.translateService.instant("You can't upload files of this type."));
                } else {
                    done();
                }
            },
        };
    }

    isDownloadFileAvailable(url: string): boolean {
        return (
            url.startsWith('https://socialboardsdev2.blob.core.windows.net') ||
            url.startsWith('https://socialboardsbproduction.blob.core.windows.net') ||
            url.startsWith('https://socialboardsstaging.blob.core.windows.net') ||
            url.startsWith('https://files.socialboards.com')
        );
    }

    onFailedUploading(event: Event): void {
        const elements: HTMLElement[] = Array.from(document.querySelectorAll('.dz-preview'));
        for (const element of elements) {
            const filename = element.querySelectorAll('.dz-filename span')[0].textContent;
            const dzErrorMessage = element.querySelectorAll('.dz-error-message span')[0];
            const errorText =
                typeof event[1] === 'string'
                    ? event[1]
                    : event[1].message || this.translateService.instant('Unknown error');

            if (filename === event[0].name) {
                dzErrorMessage.textContent = errorText;
            }
        }
    }

    uploadAsyncWithProgress(fileOrBlob: File | Blob, progressSubject, cancellationToken): Promise<any> {
        return new Promise((resolve, reject) => {
            const fd = new FormData();
            fd.append('file', fileOrBlob);

            const url = `${environment.uploadsUrl}ticketAttachments`;
            const req = new HttpRequest('POST', url, fd, {
                reportProgress: true,
            });
            const subscription = this.httpClient.request(req).subscribe({
                next: (httpEvent) => {
                    switch (httpEvent.type) {
                        case HttpEventType.UploadProgress:
                            if (progressSubject) {
                                progressSubject.next(Math.round((100 * httpEvent.loaded) / httpEvent.total));
                            }
                            break;
                        case HttpEventType.Response:
                            resolve(httpEvent.body);
                            break;
                    }
                },
                error: (err) => {
                    progressSubject.error(err);
                    reject(err);
                },
                complete: () => {
                    progressSubject.complete();
                    subscription.unsubscribe();
                    if (cancellationToken) {
                        cancellationToken.removeListener(() => {
                            subscription.unsubscribe();
                        });
                    }
                },
            });
            if (cancellationToken) {
                cancellationToken.addListener(() => {
                    subscription.unsubscribe();
                });
            }
        });
    }

    uploadFile(file: File | Blob): Observable<FileModelExtended> {
        const fd: FormData = new FormData();
        fd.append('file', file);

        return this.httpClient.post<FileModelExtended>(environment.uploadsUrl + 'attachments/', fd);
    }

    uploadTicketAttachment(file: File): Observable<AttachmentBase> {
        const fd: FormData = new FormData();
        fd.append('file', file);

        return this.httpClient.post<AttachmentBase>(environment.uploadsUrl + 'ticketAttachments/', fd);
    }

    uploadImage(file: Blob): Observable<FileModelExtended[]> {
        const fd = new FormData();
        fd.append('file', file);
        return this.httpClient.post<FileModelExtended[]>(environment.uploadsUrl + 'attachments/images', fd);
    }
}
